/**
 * Hourly board status vocabulary — server category to reader words and colour tokens (§4.10, §5.2).
 *
 * The bands are the sewing boards' own: `ON_TARGET` above 95% of the plan, `NEAR_TARGET` from 70%,
 * `BELOW_TARGET` under it. They are decided in ADB and only mapped here — no threshold lives in
 * this file. Reusing the floor and factory boards' numbers is the point: three boards hang on the
 * same wall, and amber has to mean one thing across all of them.
 *
 * `PENDING` is the state legacy could not express. An hour that has not happened and an hour that
 * produced nothing were both a plain zero, so the old grid drew eleven columns of which seven were
 * a prediction of nothing. `is_elapsed` separates them (server `design-notes.md`).
 */

import { STATUS_TONES, type StatusTone } from '../sewing-line-status';

export type CellStatus =
  | 'ON_TARGET'
  | 'NEAR_TARGET'
  | 'BELOW_TARGET'
  | 'NO_OUTPUT'
  | 'NO_TARGET'
  | 'PENDING';

/** What each state means in the words a supervisor uses on the floor. */
export const CELL_STATUS_LABEL: Record<CellStatus, string> = {
  ON_TARGET: 'Met the hourly plan',
  NEAR_TARGET: 'Just under the hourly plan',
  BELOW_TARGET: 'Well under the hourly plan',
  NO_OUTPUT: 'Nothing recorded this hour',
  NO_TARGET: 'No hourly plan',
  PENDING: 'Hour not started',
};

export function cellStatus(value: unknown): CellStatus {
  switch (value) {
    case 'ON_TARGET':
    case 'NEAR_TARGET':
    case 'BELOW_TARGET':
    case 'NO_OUTPUT':
    case 'NO_TARGET':
    case 'PENDING':
      return value;
    default:
      return 'NO_TARGET';
  }
}

/**
 * Category to colour. `NO_OUTPUT` is `bad` and not `idle`: on an hour that has already finished,
 * nothing recorded is the worst reading on the board, not a blank. `PENDING` and `NO_TARGET` are
 * the genuinely empty ones — there is no answer yet, and colouring them would invent one.
 */
export function cellTone(status: CellStatus): StatusTone {
  switch (status) {
    case 'ON_TARGET':
      return STATUS_TONES.good;
    case 'NEAR_TARGET':
      return STATUS_TONES.warn;
    case 'BELOW_TARGET':
    case 'NO_OUTPUT':
      return STATUS_TONES.bad;
    default:
      return STATUS_TONES.idle;
  }
}

/** An hour with no answer yet draws an empty track — no fill, no plan tick, no figure. */
export function isPending(status: CellStatus): boolean {
  return status === 'PENDING';
}
